import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import bitmapFonts from 'src/lib/bitmapFonts';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import uiConfig from 'src/lib/ui/config';
import i18n from 'src/lib/i18n/i18n';
import { Match2ShopBundlesPackRewards } from './Match2ShopBundlesPackRewards';
import StateObserver, { user, now } from 'src/StateObserver';
import { parseAmount } from 'src/replicant/utils/beautifyNumber';
import { ProductId } from 'src/replicant/iap';
import {
  shopBundlesGetCoinsReward,
  shopBundlesGetGemsReward,
  shopBundlesGetPack,
} from 'src/replicant/match2/shopbundles/match2-shopbundles.getters';
import { shopBundlesGetProductPriceStr } from './match2-shopbundles.utils';
import { processMatch2Rewards } from 'src/replicant/match2/rewards/match2-rewards.getters';
import { getProductName } from 'src/game/iap/components/Config';
import { realMoneyPriceToReadableString } from 'src/lib/iap/iapConversion';
import { Match2Timer } from '../components/Match2Timer';
import { downscale } from 'src/conf/scale';
import { getRemainingStarterPackPromoTime } from 'src/replicant/starterpack/starterpack.getters';

export type Match2ShopBundlesPackParams = {
  productId: ProductId;
  onPurchase: (productId: ProductId) => void;
};

export class Match2ShopBundlesPack extends View {
  public readonly bg: ImageScaleView;
  public readonly name: LangBitmapFontTextView;
  public readonly chest: ImageScaleView;
  public readonly coins: ImageScaleView;
  public readonly gems: LangBitmapFontTextView;
  public readonly promoTag: ImageScaleView;
  public rewards?: Match2ShopBundlesPackRewards[];
  public purchaseButton: ButtonScaleViewWithText;
  public productId!: ProductId;
  public onPurchase!: (productId: ProductId) => void;
  public timer?: Match2Timer;

  constructor(opts: ViewOpts) {
    super({
      width: 670,
      height: 291,
      ...opts,
    });

    this.bg = new ImageScaleView({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      image: 'assets/ui/match2/shopbundles/shopbundles_box_blue.png',
      imgScale: 0.66,
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 45, right: 45 },
      },
    });

    this.chest = new ImageScaleView({
      superview: this,
      width: 212,
      height: 164,
      x: 40,
      y: -10,
      image: 'assets/ui/match2/shopbundles/shopbundles_chest_1.png',
      scaleMethod: 'contain',
    });

    this.coins = new ImageScaleView({
      superview: this,
      width: downscale(480),
      height: downscale(248),
      x: 14,
      y: 4,
      image: 'assets/ui/match2/shopbundles/shopbundles_coins.png',
      scaleMethod: 'contain',
    });

    this.gems = new LangBitmapFontTextView({
      superview: this,
      x: 30,
      y: 110,
      align: 'center',
      verticalAlign: 'center',
      size: 32,
      font: 'Title',
      width: 200,
      height: 66,
    });

    this.promoTag = new ImageScaleView({
      superview: this,
      scaleMethod: 'contain',
      centerAnchor: true,
      centerOnOrigin: true,
    });

    this.name = new LangBitmapFontTextView({
      superview: this,
      x: 40,
      y: 180,
      align: 'left',
      verticalAlign: 'center',
      size: 32,
      font: 'Title',
      width: 350,
      height: 66,
    });

    this.purchaseButton = new ButtonScaleViewWithText({
      superview: this,
      tag: 'exchangeBtn',
      ...uiConfig.buttons.primary,
      image: 'assets/ui/match2/shopbundles/shopbundles_button.png',
      textColor: '#ffffff',
      fontSize: 30,
      labelOffsetX: -3,
      labelOffsetY: -1,
      font: bitmapFonts('Body'),
      x: 420,
      y: 180,
      width: 221,
      height: 66,
      pressedOffsetY: 2,
      localeText: () => i18n('currencyExchange.gemsShop.exchangeButton'),
      onClick: async () => this.onPurchase(this.productId),
      tracking: { feature: 'gems-shop', $subFeature: 'gems-shop-exchange' },
    });
  }

  public setup(params: Match2ShopBundlesPackParams) {
    this.productId = params.productId;
    this.onPurchase = params.onPurchase;
    this.updateBg();
    this.updateProductName();
    this.updatePriceText();
    this.updateGemsText();
    this.updatePromoTag();
    this.updateRewards();
    this.updateTimer();
  }

  private updateBg() {
    const pack = shopBundlesGetPack(this.productId);
    const regular = 'assets/ui/match2/shopbundles/shopbundles_box_blue.png';
    const imageMap: Record<string, string> = {
      sale: 'assets/ui/match2/shopbundles/shopbundles_box_purple.png',
      bestValue: 'assets/ui/match2/shopbundles/shopbundles_box_red.png',
      starterPackPromo:
        'assets/ui/match2/shopbundles/shopbundles_box_purple.png',
    };
    const image = imageMap[pack.tag ?? ''] ?? regular;
    this.bg.updateOpts({ image });
  }

  private updateProductName() {
    this.name.localeText = () => getProductName(this.productId);
  }

  private updateGemsText() {
    const gemsReward = shopBundlesGetGemsReward(this.productId);
    const coinsReward = shopBundlesGetCoinsReward(this.productId);
    const gems = parseAmount(gemsReward);
    const text = `x${gems}`;
    this.gems.localeText = () => text;
    this.gems.updateOpts({ visible: gemsReward > 0 });
    this.chest.updateOpts({ visible: gemsReward > 0 });
    this.coins.updateOpts({ visible: gemsReward === 0 && coinsReward > 0 });
  }

  private updatePriceText() {
    const pack = shopBundlesGetPack(this.productId);
    if (pack?.fixedPrice) {
      this.purchaseButton.show();
      this.purchaseButton.localeText = () =>
        realMoneyPriceToReadableString(String(pack.fixedPrice), 'JPY');
      return;
    }

    const price = shopBundlesGetProductPriceStr(this.productId);
    if (price) {
      this.purchaseButton.show();
      this.purchaseButton.localeText = () => price;
    } else {
      // If price is not defined, the product is not present in the catalog
      // Instead of throwing an error, it is being reported to Sentry, so the Ui does not break
      this.purchaseButton.hide();
    }
  }

  private updatePromoTag() {
    const pack = shopBundlesGetPack(this.productId);
    if (!pack?.tag) {
      this.promoTag.hide();
      return;
    }

    const locale = StateObserver.getLocale() === 'en' ? 'en' : 'ja';
    this.promoTag.show();
    switch (pack.tag) {
      case 'sale':
      case 'starterPackPromo':
        this.promoTag.updateOpts({
          image: `assets/ui/match2/shopbundles/shopbundles_sale_${locale}.png`,
          width: 102,
          height: 102,
          x: -10,
          y: -10,
        });
        break;
      case 'mostPopular':
        this.promoTag.updateOpts({
          image: `assets/ui/match2/shopbundles/shopbundles_mostpopular_${locale}.png`,
          width: 243,
          height: 160,
          x: -23,
          y: -46,
        });
        break;
      case 'bestValue':
        this.promoTag.updateOpts({
          image: `assets/ui/match2/shopbundles/shopbundles_bestvalue_${locale}.png`,
          width: 243,
          height: 160,
          x: -23,
          y: -46,
        });
        break;
      case 'hidden':
        this.removeFromSuperview();
        break;
      default:
        this.hide();
    }
  }

  private updateRewards() {
    const pack = shopBundlesGetPack(this.productId);
    if (!pack) return;
    const processed = processMatch2Rewards(pack.rewards, now());

    if (this.rewards) {
      for (const reward of this.rewards) {
        reward.removeFromSuperview();
      }
    }

    this.rewards = [];

    const boosters = processed.filter((reward) => reward.type === 'boosters');
    const powerups = processed.filter((reward) => reward.type === 'powerups');
    const energy = processed.filter((reward) => reward.type === 'energy');
    const coins = processed.filter((reward) => reward.type === 'bPoints');

    if (boosters.length) {
      const reward = new Match2ShopBundlesPackRewards({
        superview: this,
        x: 0,
        y: 0,
      });
      reward.setup(boosters);
      this.rewards.push(reward);
    }

    if (powerups.length) {
      const reward = new Match2ShopBundlesPackRewards({
        superview: this,
        x: 0,
        y: 0,
      });
      reward.setup(powerups);
      this.rewards.push(reward);
    }

    if (energy.length) {
      const reward = new Match2ShopBundlesPackRewards({
        superview: this,
        x: 0,
        y: 0,
      });
      reward.setup(energy, boosters.length + powerups.length === 0);
      this.rewards.push(reward);
    }

    if (coins.length) {
      const reward = new Match2ShopBundlesPackRewards({
        superview: this,
        x: 0,
        y: 0,
      });
      reward.setup(coins, boosters.length + powerups.length === 0);
      this.rewards.push(reward);
    }

    let x = this.style.width - 30;

    for (const reward of this.rewards) {
      reward.updateOpts({
        x: x - reward.style.width,
        y: 20,
      });
      x -= reward.style.width + 8;
    }
  }

  private updateTimer() {
    const pack = shopBundlesGetPack(this.productId);
    if (pack.tag !== 'starterPackPromo') {
      this.timer?.stopAutoUpdate();
      this.timer?.hide();
      return;
    }

    if (!this.timer) {
      this.timer = new Match2Timer({
        superview: this,
        x: 180,
        y: -5,
        roundUp: true,
        scale: 0.8,
      });
      this.timer.setRemainingTimeGetter(() =>
        getRemainingStarterPackPromoTime(user(), now()),
      );
    }
    this.timer.show();
    this.timer.autoUpdate();
  }
}
